/**
 * Adapter de dictado por voz para Web
 *
 * Implementa VoiceInputPort usando la Web Speech API
 * (SpeechRecognition / webkitSpeechRecognition).
 */

import type { VoiceInputCallbacks, VoiceInputPort } from '@pepi/core'

interface SpeechResultAlternative {
  transcript: string
}

interface SpeechRecognitionEventLike {
  results: ArrayLike<ArrayLike<SpeechResultAlternative>>
}

interface SpeechRecognitionLike {
  lang: string
  interimResults: boolean
  continuous: boolean
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onend: (() => void) | null
  onerror: (() => void) | null
  start: () => void
  stop: () => void
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return w.SpeechRecognition || w.webkitSpeechRecognition || null
}

/**
 * Implementación de VoiceInputPort para Web usando Web Speech API
 */
export class WebVoiceInputAdapter implements VoiceInputPort {
  private recognition: SpeechRecognitionLike | null = null

  isSupported(): boolean {
    return getRecognitionCtor() !== null
  }

  start(lang: string, callbacks: VoiceInputCallbacks): void {
    const Ctor = getRecognitionCtor()
    if (!Ctor) return

    const recognition = new Ctor()
    recognition.lang = lang
    recognition.interimResults = true
    recognition.continuous = false

    recognition.onresult = (event: SpeechRecognitionEventLike) => {
      let text = ''
      for (let i = 0; i < event.results.length; i++) {
        text += event.results[i][0]?.transcript ?? ''
      }
      callbacks.onTranscriptChange(text)
    }

    recognition.onend = () => {
      this.cleanup()
      callbacks.onEnd()
    }

    recognition.onerror = () => {
      this.cleanup()
      callbacks.onError(new Error('Error de reconocimiento de voz'))
    }

    this.recognition = recognition
    recognition.start()
  }

  stop(): void {
    if (!this.recognition) return
    // Guardamos la referencia porque cleanup() la limpia antes de notificar
    const recognition = this.recognition
    const onend = recognition.onend
    this.cleanup()
    recognition.stop()
    onend?.()
  }

  private cleanup(): void {
    if (!this.recognition) return
    this.recognition.onresult = null
    this.recognition.onend = null
    this.recognition.onerror = null
    this.recognition = null
  }
}

export function createWebVoiceInputAdapter(): VoiceInputPort {
  return new WebVoiceInputAdapter()
}
